import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const MODULOS = ['Autenticación','Clientes','Citas','Servicios','Profesionales','Sucursales','Usuarios','Horarios','Notas','Configuración','Pagos','CRM','Difusión'];

const ROL_BADGE = {
  superadmin: { bg: '#fef3c7', color: '#92400e', label: 'Superadmin' },
  admin:      { bg: '#dbeafe', color: '#1e40af', label: 'Admin'      },
  usuario:    { bg: '#f3f4f6', color: '#374151', label: 'Usuario'    },
};

const MOD_COLORS = {
  'Autenticación': '#ede9fe', 'Clientes': '#dcfce7', 'Citas': '#dbeafe',
  'Servicios': '#fef9c3',    'Profesionales': '#ffedd5', 'Sucursales': '#e0f2fe',
  'Usuarios': '#fce7f3',     'Horarios': '#f0fdf4',  'Notas': '#fdf4ff',
  'Configuración': '#f1f5f9','Pagos': '#ecfdf5',     'CRM': '#eff6ff',     'Difusión': '#fdf4ff',
};
const MOD_TEXT = {
  'Autenticación': '#5b21b6', 'Clientes': '#166534', 'Citas': '#1e40af',
  'Servicios': '#854d0e',     'Profesionales': '#9a3412', 'Sucursales': '#0369a1',
  'Usuarios': '#9d174d',      'Horarios': '#166534',  'Notas': '#7e22ce',
  'Configuración': '#334155', 'Pagos': '#065f46',     'CRM': '#1d4ed8',    'Difusión': '#7c3aed',
};

const fmt = iso => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-EC', { day:'2-digit', month:'short', year:'numeric' })
    + ' ' + d.toLocaleTimeString('es-EC', { hour:'2-digit', minute:'2-digit' });
};

export default function HistorialAcciones() {
  const { user } = useAuth();
  const esSuperAdmin = user?.rol === 'superadmin';
  const esAdmin = user?.rol === 'admin';
  const sinAcceso = !esSuperAdmin && !esAdmin && !user?.puede_ver_historial;

  const [logs,     setLogs]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [modulo,   setModulo]   = useState('');
  const [desde,    setDesde]    = useState('');
  const [hasta,    setHasta]    = useState('');

  // Solo superadmin
  const [tenants,      setTenants]      = useState([]);
  const [tenantSel,    setTenantSel]    = useState('');
  const [loadTenants,  setLoadTenants]  = useState(false);

  const fetchTenants = useCallback(async () => {
    if (!esSuperAdmin) return;
    setLoadTenants(true);
    try {
      const { data } = await api.get('/activity-logs/tenants');
      setTenants(data);
      if (data.length > 0) setTenantSel(String(data[0].id));
    } catch { toast.error('Error al cargar empresas'); }
    finally { setLoadTenants(false); }
  }, [esSuperAdmin]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, ...(modulo && { modulo }), ...(desde && { desde }), ...(hasta && { hasta }) };
      const url = esSuperAdmin && tenantSel
        ? `/activity-logs/tenant/${tenantSel}`
        : '/activity-logs';
      const { data } = await api.get(url, { params });
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error('Error al cargar historial'); }
    finally { setLoading(false); }
  }, [page, modulo, desde, hasta, esSuperAdmin, tenantSel]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);
  useEffect(() => { if (!esSuperAdmin || tenantSel) fetchLogs(); }, [fetchLogs]);

  const handleFilter = e => { e.preventDefault(); setPage(1); fetchLogs(); };
  const handleReset  = () => { setModulo(''); setDesde(''); setHasta(''); setPage(1); };

  const card = { background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', padding:'1.5rem' };
  const inputStyle = { padding:'0.5rem 0.75rem', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'0.875rem', outline:'none', background:'#fff' };
  const btnStyle = (color='#3b82f6') => ({ padding:'0.5rem 1.1rem', background:color, color:'#fff', border:'none', borderRadius:'8px', fontSize:'0.875rem', fontWeight:'600', cursor:'pointer' });

  if (sinAcceso) {
    return (
      <Layout>
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh' }}>
          <div style={{ textAlign:'center', padding:'2rem' }}>
            <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🔒</div>
            <p style={{ fontWeight:'700', color:'#374151' }}>No tienes permisos para ver el historial.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding:'1.5rem', maxWidth:'1200px', margin:'0 auto' }}>

        {/* Cabecera */}
        <div style={{ marginBottom:'1.5rem' }}>
          <h1 style={{ fontSize:'1.5rem', fontWeight:'800', color:'#111827', marginBottom:'0.25rem' }}>
            📋 Historial de Acciones
          </h1>
          <p style={{ color:'#6b7280', fontSize:'0.875rem' }}>
            Registro de actividad de los últimos 15 días. Solo lectura — ningún registro puede ser modificado o eliminado.
          </p>
        </div>

        {/* Aviso solo lectura */}
        <div style={{ display:'flex', gap:'0.75rem', padding:'0.85rem 1.1rem', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'10px', marginBottom:'1.5rem', fontSize:'0.875rem', color:'#92400e' }}>
          <span>🔒</span>
          <span>Este historial es <strong>inmutable</strong>. No es posible editar, eliminar ni exportar registros desde esta pantalla. Los registros se eliminan automáticamente a los 15 días.</span>
        </div>

        {/* Selector de empresa (solo superadmin) */}
        {esSuperAdmin && (
          <div style={{ ...card, marginBottom:'1.25rem' }}>
            <label style={{ fontWeight:'600', fontSize:'0.875rem', color:'#374151', marginRight:'0.75rem' }}>
              🏢 Ver historial de:
            </label>
            {loadTenants ? <span style={{ color:'#6b7280', fontSize:'0.875rem' }}>Cargando empresas...</span> : (
              <select
                value={tenantSel}
                onChange={e => { setTenantSel(e.target.value); setPage(1); }}
                style={{ ...inputStyle, minWidth:'280px' }}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.nombre_negocio} — {t.total_acciones} acciones
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Filtros */}
        <form onSubmit={handleFilter} style={{ ...card, marginBottom:'1.25rem' }}>
          <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <label style={{ display:'block', fontSize:'0.78rem', fontWeight:'600', color:'#374151', marginBottom:'0.3rem' }}>Módulo</label>
              <select value={modulo} onChange={e => setModulo(e.target.value)} style={inputStyle}>
                <option value=''>Todos</option>
                {MODULOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'0.78rem', fontWeight:'600', color:'#374151', marginBottom:'0.3rem' }}>Desde</label>
              <input type='date' value={desde} onChange={e => setDesde(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'0.78rem', fontWeight:'600', color:'#374151', marginBottom:'0.3rem' }}>Hasta</label>
              <input type='date' value={hasta} onChange={e => setHasta(e.target.value)} style={inputStyle} />
            </div>
            <button type='submit' style={btnStyle()}>Filtrar</button>
            <button type='button' onClick={handleReset} style={{ ...btnStyle('#6b7280') }}>Limpiar</button>
          </div>
        </form>

        {/* Tabla */}
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <span style={{ fontSize:'0.875rem', color:'#6b7280' }}>
              {loading ? 'Cargando...' : `${total} registro${total !== 1 ? 's' : ''} encontrado${total !== 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'#6b7280' }}>⏳ Cargando historial...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'#9ca3af' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:'0.5rem' }}>📭</div>
              <p>No hay registros para los filtros seleccionados.</p>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.875rem' }}>
                <thead>
                  <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                    {esSuperAdmin && <th style={th}>Empresa</th>}
                    <th style={th}>Fecha y hora</th>
                    <th style={th}>Usuario</th>
                    <th style={th}>Rol</th>
                    <th style={th}>Módulo</th>
                    <th style={th}>Acción</th>
                    <th style={th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const rolBadge = ROL_BADGE[log.usuario_rol] || ROL_BADGE.usuario;
                    const modBg   = MOD_COLORS[log.modulo] || '#f3f4f6';
                    const modTxt  = MOD_TEXT[log.modulo]   || '#374151';
                    return (
                      <tr key={log.id} style={{ borderBottom:'1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        {esSuperAdmin && <td style={td}><span style={{ fontWeight:'600', color:'#1d4ed8' }}>{log.nombre_negocio}</span></td>}
                        <td style={{ ...td, whiteSpace:'nowrap', color:'#374151' }}>{fmt(log.created_at)}</td>
                        <td style={td}>
                          <div style={{ fontWeight:'600', color:'#111827' }}>{log.usuario_nombre || '—'}</div>
                          <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>{log.usuario_email}</div>
                        </td>
                        <td style={td}>
                          <span style={{ background:rolBadge.bg, color:rolBadge.color, padding:'0.2rem 0.6rem', borderRadius:'999px', fontSize:'0.75rem', fontWeight:'600' }}>
                            {rolBadge.label}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ background:modBg, color:modTxt, padding:'0.2rem 0.6rem', borderRadius:'999px', fontSize:'0.75rem', fontWeight:'600' }}>
                            {log.modulo}
                          </span>
                        </td>
                        <td style={{ ...td, fontFamily:'monospace', color:'#1e40af', fontSize:'0.82rem' }}>
                          {log.accion}
                          {log.descripcion && <div style={{ fontFamily:'sans-serif', color:'#6b7280', fontSize:'0.78rem', marginTop:'0.1rem' }}>{log.descripcion}</div>}
                        </td>
                        <td style={{ ...td, color:'#9ca3af', fontSize:'0.78rem', whiteSpace:'nowrap' }}>{log.ip_address || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {pages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:'0.4rem', marginTop:'1.25rem', flexWrap:'wrap' }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={{ ...btnStyle('#e5e7eb'), color:'#374151', opacity: page===1 ? 0.5 : 1 }}>‹ Anterior</button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                const p = page <= 4 ? i+1 : page - 3 + i;
                if (p < 1 || p > pages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)} style={{ ...btnStyle(p===page ? '#3b82f6' : '#f3f4f6'), color: p===page ? '#fff' : '#374151', minWidth:'36px' }}>{p}</button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages} style={{ ...btnStyle('#e5e7eb'), color:'#374151', opacity: page===pages ? 0.5 : 1 }}>Siguiente ›</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const th = { padding:'0.65rem 0.85rem', textAlign:'left', fontWeight:'600', color:'#374151', fontSize:'0.8rem', textTransform:'uppercase', letterSpacing:'0.04em' };
const td = { padding:'0.7rem 0.85rem', verticalAlign:'top' };
